'use client';

import { c as _c } from "react/compiler-runtime";
import { Skeleton } from '@common/components';
import SvgTabMoreLeft from '@common/icon/SvgTabMoreLeft';
import SvgTabMoreRight from '@common/icon/SvgTabMoreRight';
import { cn } from '@core/utils';
import { useSuspenseQuery } from '@tanstack/react-query';
import { type ReactNode, Suspense, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useInView } from 'react-intersection-observer';
import { AuthQueryOptions, UnauthQueryOptions } from '@/api';
import { DesktopPageShell } from '@/components/template/DesktopPageShell';
import { ROUTES, SELECTOR } from '@/constants';
import { getNowInKorea } from '@/utils/checkTodayInRange';
import { BENEFIT_HOME_TABS_ID } from '../_constants';
import { useAffiliateClick } from '../_hooks/useAffiliateClick';
import { useHorizontalDragScroll } from '../_hooks/useHorizontalDragScroll';
import { type HotDeal, useHotDeals } from '../_hooks/useHotDeals';
import { isAffiliateNewBadgeVisible } from '../_utils/isAffiliateNewBadgeVisible';
import { selectHomeHotDeals } from '../_utils/selectHomeHotDeals';
import { MerchantProductCard } from '../merchants/_components/MerchantProductCard';
import { AFFILIATE_DISCLOSURE, MERCHANT_TAB } from '../merchants/_constants';
import { BenefitHomeSectionHeader } from './BenefitHomeSectionHeader';
import { BENEFIT_SECTION_CLASS_NAME } from './BenefitMenuSection';
import { HotDealCard } from './HotDealCard';
const HOME_HOT_DEAL_LIMIT = 10;
const HOME_PRODUCT_LIMIT = 6;
const PRODUCT_GRID_CLASS_NAME = 'mt-12 grid grid-cols-3 gap-x-12 gap-y-20 tablet:grid-cols-6';
const FALLBACK_CARD_COUNT = 4;
const CAROUSEL_CLASS_NAME = 'flex gap-12 overflow-x-auto py-4 pl-16 pr-16 hide-scrollbar webkit-scroll-touch';
const CAROUSEL_ARROW_CLASS_NAME = cn('absolute top-56 z-10 hidden -translate-y-1/2 rounded-pill opacity-0 shadow-[2px_2px_7px_rgba(0,0,0,0.2)] xl:top-84', 'transition-[opacity,filter] duration-200 motion-reduce:transition-none', 'hover:brightness-95 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-B-500 active:brightness-90', '[@media(hover:hover)_and_(pointer:fine)]:block [@media(hover:hover)_and_(pointer:fine)]:group-hover:opacity-100 [@media(hover:hover)_and_(pointer:fine)]:group-has-[:focus-visible]:opacity-100');
const CAROUSEL_ITEM_CLASS_NAME = 'flex w-104 shrink-0 xl:w-160';
const HOT_DEAL_PANEL_ID = 'benefit-hot-deal-panel';
const HOT_DEAL_PANEL_SCROLL_GAP = 16;

//* 헤더 묶음 높이는 키워드 바·포인트 바 유무로 바뀌어 고정 scroll-margin으로는 못 맞춘다.
const scrollToHotDealPanel = () => {
  const panel = document.getElementById(HOT_DEAL_PANEL_ID);
  if (!panel) {
    return;
  }
  const headerBottom = Math.max(...[SELECTOR.MOBILE_HEADER_ATTACHMENT, SELECTOR.DESKTOP_HEADER_ATTACHMENT].map(id => document.getElementById(id)?.getBoundingClientRect().bottom ?? 0));
  window.scrollTo({
    top: window.scrollY + panel.getBoundingClientRect().top - headerBottom - HOT_DEAL_PANEL_SCROLL_GAP,
    //* 긴 instant 점프는 iOS 웹뷰가 직후 sticky 헤더 프레임을 잃는다(!4691과 같은 이유).
    behavior: 'smooth'
  });
};
const SectionHeading = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex flex-col gap-8"><div className="flex flex-col gap-4"><BenefitHomeSectionHeader title={"\uC81C\uD734\uBAB0 \uC0C1\uD488"} isNew={isAffiliateNewBadgeVisible()} moreHref={ROUTES.BENEFIT_MERCHANTS.withSearchParams({
          searchParams: {
            tab: MERCHANT_TAB.PRODUCT
          }
        })} /><p className="font-600 text-Gray-600 typo-body4">제휴몰에서 결제하고 돌려받아요</p></div><p className="font-500 text-Gray-550 typo-caption2">{AFFILIATE_DISCLOSURE}</p></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const HotDealCarouselTrack = t0 => {
  const $ = _c(15);
  const {
    children
  } = t0;
  const {
    elementRef,
    canScrollPrev,
    canScrollNext,
    scrollByPage,
    dragHandlers
  } = useHorizontalDragScroll();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn(CAROUSEL_CLASS_NAME, "[@media(hover:hover)_and_(pointer:fine)]:cursor-grab");
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children || $[2] !== dragHandlers || $[3] !== elementRef) {
    t2 = <ul ref={elementRef} className={t1} {...dragHandlers}>{children}</ul>;
    $[1] = children;
    $[2] = dragHandlers;
    $[3] = elementRef;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== canScrollPrev || $[6] !== scrollByPage) {
    t3 = canScrollPrev && <button aria-label={"\uC774\uC804 \uC0C1\uD488"} onClick={() => scrollByPage(-1)} className={cn(CAROUSEL_ARROW_CLASS_NAME, "left-8")}><SvgTabMoreLeft className="size-24" originalFill={true} /></button>;
    $[5] = canScrollPrev;
    $[6] = scrollByPage;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== canScrollNext || $[9] !== scrollByPage) {
    t4 = canScrollNext && <button aria-label={"\uB2E4\uC74C \uC0C1\uD488"} onClick={() => scrollByPage(1)} className={cn(CAROUSEL_ARROW_CLASS_NAME, "right-8")}><SvgTabMoreRight className="size-24" originalFill={true} /></button>;
    $[8] = canScrollNext;
    $[9] = scrollByPage;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t2 || $[12] !== t3 || $[13] !== t4) {
    t5 = <div className="group relative -ml-16">{t2}{t3}{t4}</div>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
};
const HotDealPanel = t0 => {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h3 className="font-700 text-Gray-900 typo-body3">🔥 지금 핫딜</h3>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <div id={HOT_DEAL_PANEL_ID} className="flex flex-col gap-12 overflow-hidden rounded-large bg-gradient-to-b from-O-100 to-O-25 pb-16 pl-16 pt-12">{t1}{children}</div>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
interface HotDealFloatingButtonProps {
  isVisible: boolean;
}

//* PC는 본문 칼럼 가운데에 맞추려고 PointStickyBar처럼 셸을 그대로 쓴다.
const HotDealFloatingButton = t0 => {
  const $ = _c(7);
  const {
    isVisible
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="hidden w-300 shrink-0 xl:block" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = !isVisible;
  const t3 = isVisible ? undefined : -1;
  const t4 = !isVisible && "pointer-events-none opacity-0";
  let t5;
  if ($[1] !== t4) {
    t5 = cn("pointer-events-auto flex h-40 items-center justify-center rounded-pill border border-O-550 bg-Gray-white/50 px-12 font-600 text-Gray-black shadow-[2px_2px_7px_rgba(0,0,0,0.2)] backdrop-blur-lg typo-body4 [-webkit-backdrop-filter:blur(16px)] xl:w-180", "transition-[opacity,background-color] duration-200 hover:bg-O-25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-B-500 active:bg-O-50 motion-reduce:transition-none", t4);
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== t2 || $[4] !== t3 || $[5] !== t5) {
    t6 = <div className="pointer-events-none fixed inset-x-0 bottom-[calc(var(--bottom-inset,0px)+16px)] z-10 xl:bottom-32"><DesktopPageShell variant="rail" rightAside={t1}><div className="flex justify-center"><button aria-hidden={t2} tabIndex={t3} onClick={scrollToHotDealPanel} className={t5}>🔥 지금 핫딜 상품 보기</button></div></DesktopPageShell></div>;
    $[3] = t2;
    $[4] = t3;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
};
interface AffiliateProductGridProps {
  isGuest: boolean;
}
const AffiliateProductGrid = t0 => {
  const $ = _c(8);
  const {
    isGuest
  } = t0;
  let t1;
  if ($[0] !== isGuest) {
    t1 = isGuest ? UnauthQueryOptions["get:/wello/v1/allows/affiliate/gift-products/use-tab"]() : AuthQueryOptions["get:/wello/v1/auth/affiliate/gift-products/use-tab"]();
    $[0] = isGuest;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = {
      ...t1,
      select: _temp
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: products
  } = useSuspenseQuery(t2);
  if (products.length === 0) {
    return null;
  }
  let t3;
  if ($[4] !== products) {
    t3 = products.map(_temp2);
    $[4] = products;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <ul className={PRODUCT_GRID_CLASS_NAME}>{t3}</ul>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
const AffiliateProductGridFallback = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <ul aria-hidden={true} className={PRODUCT_GRID_CLASS_NAME}>{Array.from({
        length: HOME_PRODUCT_LIMIT
      }, _temp3)}</ul>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
interface BenefitAffiliateProductSectionProps {
  isGuest: boolean;
}
const HotDealCarousel = t0 => {
  const $ = _c(29);
  const {
    isGuest
  } = t0;
  const {
    data: allHotDeals
  } = useHotDeals();
  const {
    startAffiliateClick
  } = useAffiliateClick();
  const hotDeals = selectHomeHotDeals(allHotDeals, getNowInKorea().format("YYYY-MM-DD"));
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      initialInView: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    ref,
    inView,
    entry
  } = useInView(t1);
  const isSectionBelow = !inView && (entry?.boundingClientRect.top ?? 0) > 0;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      initialInView: true,
      rootMargin: "-50% 0px 0px 0px"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    ref: tabsRef,
    inView: isTabsInLowerHalf
  } = useInView(t2);
  let t3;
  let t4;
  if ($[2] !== tabsRef) {
    t3 = () => {
      tabsRef(document.getElementById(BENEFIT_HOME_TABS_ID));
    };
    t4 = [tabsRef];
    $[2] = tabsRef;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  const hasFloatingTarget = hotDeals.some(_temp4);
  if (hotDeals.length === 0) {
    let t5;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <section className={BENEFIT_SECTION_CLASS_NAME}><SectionHeading /><p className="flex min-h-120 items-center justify-center rounded-medium bg-Gray-50 font-500 text-Gray-500 typo-body4">지금 추천할 핫딜이 없어요.</p></section>;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    return t5;
  }
  let t5;
  if ($[6] !== startAffiliateClick) {
    t5 = hotDeal_0 => {
      startAffiliateClick({
        target: {
          type: "hotDeal",
          affiliateHotDealId: hotDeal_0.affiliateHotDealId
        },
        entryPointType: "HOT_DEAL",
        appGuideModalPolicy: hotDeal_0.appGuideModalPolicy,
        guide: {
          merchantCode: hotDeal_0.merchantCode,
          merchantName: hotDeal_0.merchantName ?? "",
          logoImageUrl: hotDeal_0.logoImageUrl ?? "",
          modalText: hotDeal_0.appGuideModalText,
          showDetailLink: true
        }
      });
    };
    $[6] = startAffiliateClick;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const handleSelectHotDeal = t5;
  const t6 = BENEFIT_SECTION_CLASS_NAME;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <SectionHeading />;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const T0 = HotDealPanel;
  const T1 = HotDealCarouselTrack;
  let t8;
  if ($[9] !== handleSelectHotDeal) {
    t8 = hotDeal_1 => <li key={hotDeal_1.affiliateHotDealId} className={CAROUSEL_ITEM_CLASS_NAME}><HotDealCard hotDeal={hotDeal_1} isCompact={true} onSelect={() => handleSelectHotDeal(hotDeal_1)} /></li>;
    $[9] = handleSelectHotDeal;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const t9 = hotDeals.slice(0, HOME_HOT_DEAL_LIMIT).map(t8);
  let t10;
  if ($[11] !== T1 || $[12] !== t9) {
    t10 = <T1>{t9}</T1>;
    $[11] = T1;
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== T0 || $[15] !== t10) {
    t11 = <T0>{t10}</T0>;
    $[14] = T0;
    $[15] = t10;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <AffiliateProductGridFallback />;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== isGuest) {
    t13 = <Suspense fallback={t12}><AffiliateProductGrid isGuest={isGuest} /></Suspense>;
    $[18] = isGuest;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const t14 = isSectionBelow && !isTabsInLowerHalf && hasFloatingTarget;
  let t15;
  if ($[20] !== t14) {
    t15 = <HotDealFloatingButton isVisible={t14} />;
    $[20] = t14;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== ref || $[23] !== t11 || $[24] !== t13 || $[25] !== t15 || $[26] !== t6 || $[27] !== t7) {
    t16 = <section ref={ref} className={t6}>{t7}{t11}{t13}{t15}</section>;
    $[22] = ref;
    $[23] = t11;
    $[24] = t13;
    $[25] = t15;
    $[26] = t6;
    $[27] = t7;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  return t16;
};
const HotDealCarouselFallback = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SectionHeading />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <section className={BENEFIT_SECTION_CLASS_NAME}>{t0}<HotDealPanel><ul aria-hidden={true} className={cn(CAROUSEL_CLASS_NAME, "-ml-16")}>{Array.from({
            length: FALLBACK_CARD_COUNT
          }, _temp5)}</ul></HotDealPanel></section>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
export const BenefitAffiliateProductSection = t0 => {
  const $ = _c(3);
  const {
    isGuest
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <HotDealCarouselFallback />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== isGuest) {
    t2 = <ErrorBoundary fallback={null}><Suspense fallback={t1}><HotDealCarousel isGuest={isGuest} /></Suspense></ErrorBoundary>;
    $[1] = isGuest;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
function _temp(response) {
  return response?.context?.contents ?? [];
}
function _temp2(giftProduct) {
  return <li key={giftProduct.affiliateGiftProductLinkId} className="flex"><MerchantProductCard giftProduct={giftProduct} entryPointType="HOT_DEAL" /></li>;
}
function _temp3(_, index) {
  return <li key={index} className="flex flex-col gap-8"><Skeleton className="aspect-square w-full rounded-large" /><Skeleton typo="caption1" className="w-full rounded-xxsmall" /><Skeleton typo="body4" className="w-2/3 rounded-xxsmall" /></li>;
}
function _temp4(hotDeal) {
  return hotDeal.rewardCurrencyType !== "AFFILIATE_PARTNER_POINT";
}
function _temp5(_, index) {
  return <li key={index} className={CAROUSEL_ITEM_CLASS_NAME}><span className="flex flex-1 flex-col gap-8"><Skeleton className="aspect-square w-full rounded-xlarge" /><Skeleton typo="caption1" className="w-full rounded-xxsmall" /><Skeleton typo="body4" className="w-2/3 rounded-xxsmall" /></span></li>;
}